import { AnimatePresence, motion } from 'framer-motion';
import { TextAreaField } from '~/components/shared/forms';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Box,
  Column,
  Divider,
  InputSelect,
  Label,
  MultiSelectGroup,
  NumberField,
  RadioOption,
  Row,
} from '~/commonComponents';
import { benefitsOptions, benefitsOtherMaxLength } from '~/components/PermanentJobsV2/utils';
import { EnableDailyPayOption, EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { CompensationType, PayType } from '~/domain/PermanentJobs';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { getRateSuffix } from '../../utils/formatters';

export const EarningBenefitForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const isEnforcePostAJobFunnelImprovements = useFeatureFlag(EnforcePostAJobFunnelImprovements);
  const isDailyPayOptionEnabled = useFeatureFlag(EnableDailyPayOption);

  const watchBenefits = useWatch({ control, name: 'benefits' });
  const watchCompensationType = useWatch({ control, name: 'compensationType' });
  const watchPayType = useWatch({ control, name: 'payType' });
  const watchMaxRate = useWatch({ control, name: 'maxRate' });

  const payRateLabel = useMemo(() => getRateSuffix(watchPayType), [watchPayType]);

  // Auto-set compensationType based on maxRate when in funnel layout
  useEffect(() => {
    if (!isEnforcePostAJobFunnelImprovements) return;

    if (watchMaxRate) {
      setValue('compensationType', CompensationType.RANGE);
    } else {
      setValue('compensationType', CompensationType.FIXED);
    }
  }, [watchMaxRate, setValue, isEnforcePostAJobFunnelImprovements]);

  const handleBenefitsChange = useCallback(
    (selectedValues: string[], fieldOnChange: (...event: any[]) => void) => {
      if (!selectedValues.includes('Other')) {
        setValue?.('benefitsOther', '');
      }

      fieldOnChange(selectedValues);
    },
    [setValue],
  );

  const handleCompensationTypeChange = (type: CompensationType) => {
    setValue('compensationType', type);
    if (type === CompensationType.FIXED) {
      setValue('maxRate', '');
    }
  };

  const payTypeOptions = useMemo(() => {
    const options = [
      {
        title: isEnforcePostAJobFunnelImprovements ? 'per year' : 'Per Year',
        value: PayType.SALARY,
      },
      {
        title: isEnforcePostAJobFunnelImprovements ? 'per hour' : 'Per Hour',
        value: PayType.HOURLY,
      },
    ];
    if (isDailyPayOptionEnabled) {
      options.push({
        title: isEnforcePostAJobFunnelImprovements ? 'per day' : 'Per Day',
        value: PayType.DAILY,
      });
    }
    return options;
  }, [isDailyPayOptionEnabled, isEnforcePostAJobFunnelImprovements]);

  if (isEnforcePostAJobFunnelImprovements) {
    return (
      <Column gap={24}>
        <Column gap={12}>
          <Label text="Stated Pay" />
          <Row alignItems="center" gap={20}>
            <Column>
              <Label text="Minimum Pay" />
              <Row alignItems="center" gap={8}>
                <Typography as="span" bold size="body_large">
                  $
                </Typography>
                <Controller
                  control={control}
                  name="minRate"
                  render={({ field }) => (
                    <NumberField
                      placeholder="Enter Minimum"
                      value={field.value}
                      setValue={field.onChange}
                      currency
                      customInputStyle={{ width: '140px' }}
                      error={!!errors?.minRate}
                    />
                  )}
                />
              </Row>
            </Column>
            <Typography as="span" bold size="body_large" style={{ marginTop: 28 }}>
              —
            </Typography>
            <Column>
              <Label text="Maximum Pay" required={false} />
              <Row alignItems="center" gap={8}>
                <Typography as="span" bold size="body_large">
                  $
                </Typography>
                <Controller
                  control={control}
                  name="maxRate"
                  render={({ field }) => (
                    <NumberField
                      placeholder="Enter Maximum"
                      value={field.value}
                      setValue={field.onChange}
                      currency
                      customInputStyle={{ width: '140px' }}
                      error={!!errors?.maxRate}
                    />
                  )}
                />
              </Row>
            </Column>
            <Column>
              <Label text="Pay Type" />
              <Controller
                control={control}
                name="payType"
                render={({ field }) => (
                  <Box width={180}>
                    <InputSelect
                      placeholder="Pay type"
                      value={field.value}
                      setValue={field.onChange}
                      isSelectVisible={false}
                      options={payTypeOptions}
                    />
                  </Box>
                )}
              />
            </Column>
          </Row>
          <Typography italic color={colors.neutral[500]}>
            Leave maximum pay empty if you offer a fixed amount.
          </Typography>
        </Column>

        <Divider height={1} marginVertical={0} />

        <Column gap={12}>
          <Label text="Benefits" required={false} />
          <Typography italic color={colors.neutral[500]}>
            <Controller
              control={control}
              name="benefits"
              render={({ field }) => (
                <MultiSelectGroup
                  value={field.value}
                  variant="pill"
                  options={benefitsOptions.map((option) => ({
                    value: option.code,
                    label: option.name,
                  }))}
                  onChange={(selected) =>
                    handleBenefitsChange(
                      selected.map((s) => s.value),
                      field.onChange,
                    )
                  }
                />
              )}
            />
          </Typography>
          <AnimatePresence mode="wait">
            {watchBenefits?.includes('Other') && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Column marginTop={8} gap={10}>
                  <Typography as="span">Enter any other benefits below:</Typography>
                  <Controller
                    control={control}
                    name="benefitsOther"
                    render={({ field }) => (
                      <TextAreaField
                        {...field}
                        height={60}
                        maxLength={benefitsOtherMaxLength}
                        placeholder="Enter other benefits here"
                      />
                    )}
                  />
                </Column>
              </motion.div>
            )}
          </AnimatePresence>
        </Column>
      </Column>
    );
  }

  return (
    <Column gap={24}>
      <Column gap={12}>
        <Label text="Pay Type" required={false} />
        <Controller
          control={control}
          name="payType"
          render={({ field }) => (
            <Row gap={24}>
              <RadioOption
                text="Salary"
                checked={field.value === PayType.SALARY}
                onClick={() => field.onChange(PayType.SALARY)}
              />
              <RadioOption
                text="Hourly"
                checked={field.value === PayType.HOURLY}
                onClick={() => field.onChange(PayType.HOURLY)}
              />
              {isDailyPayOptionEnabled && (
                <RadioOption
                  text="Daily"
                  checked={field.value === PayType.DAILY}
                  onClick={() => field.onChange(PayType.DAILY)}
                />
              )}
            </Row>
          )}
        />
      </Column>
      <Column gap={12}>
        <Label text="Compensation" required={false} />
        <Column gap={16}>
          <RadioOption
            text="Fixed Amount"
            checked={watchCompensationType === CompensationType.FIXED}
            onClick={() => handleCompensationTypeChange(CompensationType.FIXED)}
          />
          <RadioOption
            text="Pay Range (Min - Max)"
            checked={watchCompensationType === CompensationType.RANGE}
            onClick={() => handleCompensationTypeChange(CompensationType.RANGE)}
          />
        </Column>
      </Column>
      {watchCompensationType && (
        <Column>
          <Label
            text={`Stated Pay ${watchCompensationType === CompensationType.RANGE ? 'Range' : ''}`}
            required
          />
          <Row alignItems="center" gap={8}>
            <Typography as="span">$</Typography>
            <Controller
              control={control}
              name="minRate"
              render={({ field }) => (
                <NumberField
                  placeholder="Amount"
                  value={field.value}
                  setValue={field.onChange}
                  currency
                  customInputStyle={{
                    width: watchCompensationType === CompensationType.FIXED ? '200px' : '150px',
                  }}
                  error={!!errors?.minRate}
                />
              )}
            />
            {watchCompensationType === CompensationType.RANGE && (
              <>
                <Typography as="span">-</Typography>
                <Typography as="span">$</Typography>
                <Controller
                  control={control}
                  name="maxRate"
                  render={({ field }) => (
                    <NumberField
                      placeholder="Amount"
                      value={field.value}
                      setValue={field.onChange}
                      currency
                      customInputStyle={{ width: '150px' }}
                      error={!!errors?.maxRate}
                    />
                  )}
                />
              </>
            )}
            <Typography as="span" size="body_large" bold>
              {payRateLabel}
            </Typography>
          </Row>
        </Column>
      )}

      <Divider height={1} marginVertical={0} />

      <Column gap={12}>
        <Label text="Benefits Information" required={false} />
        <Typography italic color={colors.neutral[500]}>
          <Controller
            control={control}
            name="benefits"
            render={({ field }) => (
              <MultiSelectGroup
                value={field.value}
                options={benefitsOptions.map((option) => ({
                  value: option.code,
                  label: option.name,
                }))}
                onChange={(selected) =>
                  handleBenefitsChange(
                    selected.map((s) => s.value),
                    field.onChange,
                  )
                }
              />
            )}
          />
        </Typography>
        <AnimatePresence mode="wait">
          {watchBenefits?.includes('Other') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Column marginTop={8} gap={10}>
                <Typography as="span">Enter any other benefits below:</Typography>
                <Controller
                  control={control}
                  name="benefitsOther"
                  render={({ field }) => (
                    <TextAreaField
                      {...field}
                      height={60}
                      maxLength={benefitsOtherMaxLength}
                      placeholder="Enter other benefits here"
                    />
                  )}
                />
              </Column>
            </motion.div>
          )}
        </AnimatePresence>
      </Column>
    </Column>
  );
};
